import { useEffect, useState } from 'react'

import { AnnualAttestationDetailsResponse, getAnnualAttestationDetails } from 'core'

import { Skeleton } from 'common/components'
import { useQueryRequestV2 } from 'common/hooks'

import ConfirmAnnualInsuranceCoverage from './confirmAnnualInsuranceCoverage'
import ConfirmAttestationForMonth from './confirmAttestationForMonth'
import ConfirmedInsuranceCoverageSuccess from './confirmedInsuranceCoverageSuccess'
import CoverageAttestationCalendar from './coverageAttestationCalendar'
import EditInsuranceCoverageModal, { EditInsurance } from './editInsuranceCoverageModal'
import SaveAnnualInsuranceCoverageModal from './saveAnnualInsuranceCoverageModal'

type AnnualInsuranceCoverageProps = {
  planPeriodId: string
  benefitEligibilityDate: string
  hasBenefitStarted: boolean
}

const AnnualInsuranceCoverage = ({
  planPeriodId,
  benefitEligibilityDate,
  hasBenefitStarted,
}: AnnualInsuranceCoverageProps) => {
  const [saveAnnualInsuranceCoverageModalVisible, setSaveAnnualInsuranceCoverageModalVisible] = useState(false)
  const [editInsurance, setEditInsurance] = useState<EditInsurance | null>()

  const {
    data: annualAttestationDetails,
    sendQuery,
    isLoading,
  } = useQueryRequestV2<AnnualAttestationDetailsResponse>({})

  useEffect(() => {
    if (planPeriodId) {
      ;(async () => {
        await sendQuery(() => getAnnualAttestationDetails(planPeriodId))
      })()
    }
  }, [planPeriodId])

  return (
    <>
      <Skeleton active={true} loading={isLoading}>
        {annualAttestationDetails && (
          <>
            {!annualAttestationDetails.hasConfirmedAnnualAttestation && (
              <ConfirmAnnualInsuranceCoverage
                onConfirmCoverage={() => setSaveAnnualInsuranceCoverageModalVisible(true)}
              />
            )}

            {annualAttestationDetails.hasConfirmedAnnualAttestation && !hasBenefitStarted && (
              <ConfirmedInsuranceCoverageSuccess benefitEligibilityDate={benefitEligibilityDate} />
            )}

            {annualAttestationDetails.hasConfirmedAnnualAttestation &&
              hasBenefitStarted &&
              annualAttestationDetails.monthRequiresAttestationDecision &&
              annualAttestationDetails.monthRequiresAttestationDecision.mecData && (
                <ConfirmAttestationForMonth
                  startDate={annualAttestationDetails.monthRequiresAttestationDecision.mecData.startDate}
                  providerName={annualAttestationDetails.monthRequiresAttestationDecision.mecData.providerName}
                  policyName={annualAttestationDetails.monthRequiresAttestationDecision.mecData.policyName}
                  onSaved={async () => {
                    await sendQuery(() => getAnnualAttestationDetails(planPeriodId))
                  }}
                  onNotConfirmed={(startDate) =>
                    setEditInsurance({ startDate, coveredByAnIndividualHealthInsurancePolicy: false })
                  }
                />
              )}

            {annualAttestationDetails.hasConfirmedAnnualAttestation &&
              hasBenefitStarted &&
              !annualAttestationDetails.monthRequiresAttestationDecision && (
                <CoverageAttestationCalendar
                  months={annualAttestationDetails.months}
                  onClick={(month) => {
                    if (month.mecData) {
                      setEditInsurance({
                        startDate: month.mecData.startDate,
                        providerName: month.mecData.providerName,
                        policyName: month.mecData.policyName,
                      })
                    }
                  }}
                />
              )}
          </>
        )}
      </Skeleton>

      <SaveAnnualInsuranceCoverageModal
        visible={saveAnnualInsuranceCoverageModalVisible}
        planPeriodId={planPeriodId}
        onSaved={async () => {
          await sendQuery(() => getAnnualAttestationDetails(planPeriodId))
          setSaveAnnualInsuranceCoverageModalVisible(false)
        }}
        onCancel={() => setSaveAnnualInsuranceCoverageModalVisible(false)}
      />

      {editInsurance && (
        <EditInsuranceCoverageModal
          visible={true}
          planPeriodId={planPeriodId}
          editInsurance={editInsurance}
          onSaved={async () => {
            await sendQuery(() => getAnnualAttestationDetails(planPeriodId))
            setEditInsurance(null)
          }}
          onCancel={() => setEditInsurance(null)}
        />
      )}
    </>
  )
}

export default AnnualInsuranceCoverage
